import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Query } from '@material-table/core';
import { Container, MailDialog, MTable, RepealableName } from '../../../../../Components';
import { useFilter } from '../../../../../Components/ListFilter/useFilter';
import { IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import moment from 'moment';
import { Button, RadioGroup, Tooltip } from '@material-ui/core'
import { ArrowUpward, Edit } from '@material-ui/icons'
import { ObservationFilter } from './Filters';
import { ComplianceReportCheckbox } from '../../../Components/Compliance/Checkbox';
import { useIsMounted } from '../../../../../Hooks/useIsMounted';
import { useStyles } from '../CarryLK/CreateDirectCompliance.style';
import EditObservationModal from './EditObservationModal';
import { AccessLevel, LawPortalFeature, Module, UserContext } from '../../../../../Context/UserContext/UserContext';

export const ObservationView = ({PlanData,filters}) => {
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const tableRef = React.useRef<any>(null);
  const isMounted = useIsMounted();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [tableKey, setTableKey] = useState<number>(0);

  const handleEditClick = useCallback((rowData: any) => {
    setSelectedRowData(rowData);
    setModalOpen(true);
  }, []);

  const handleSaveSuccess = useCallback(() => {
    tableRef.current?.onQueryChange();
  }, []);

  useEffect(() => {
    if (isMounted) {
      // Increment table key to force remount and clear cached data
      // This will automatically trigger a fresh data fetch
      setTableKey(prev => prev + 1);
    }
  }, [filters, isMounted]);


  const handleDataFetch = useCallback(
    async (query: Query<any>) => {
      try {
        const response = await api.searchObservation<any>(PlanData?.id,{
          orderDirection: query.orderDirection === 'asc' ? OrderDirection.Ascending : OrderDirection.Descending,
          orderBy: query.orderBy !== undefined ? query.orderBy.field : 'createdAt',
          page: query.page,
          pageSize: query.pageSize,
          search: query.search,
          filters: { ...filters, status: filters.status?.length ? filters.status[0] : null },
        } as IGridQueryRequest);
        return {
          data: response?.data || [],
          page: response?.page || 0,
          totalCount: response?.totalCount || 0,
        };
      } catch (error) {
        console.error('Error fetching observations:', error);
        throw error;
      }
    },
    [api, filters],
  );
  const {
    complianceUserFor,
    hasAccessToFeature,
    getCompanyUnitName, hasAccessToModule } = useContext(UserContext);
  const canDownloadAllReports = hasAccessToFeature(LawPortalFeature.Manage, AccessLevel.Manage);
  const canDownloadReport = (row: any): boolean => {
  if (canDownloadAllReports) return true;

  const { customerCompanyUnitId, legislationType, lawId, requirementId } = row ?? {};
  const list = legislationType === 0 ? complianceUserFor.laws : complianceUserFor.requirements;
  const id = legislationType === 0 ? lawId : requirementId;

  return list.some((x) => x.customerCompanyUnitId === customerCompanyUnitId && x.id === id);
};

  const renderDownloadAction = (row: any) => {
    return {
      tooltip: formatMessage({ id: 'law-portal.statistics.table.download-compliance' }),
      icon: 'picture_as_pdf',
      onClick: () => {
        row?.legislationType === 0 ? api.downloadLawComplianceReport(row.lawId, row.reportId) : api.downloadRequirementComplianceReport(row.requirementId, row.reportId)
      },
      disabled: !canDownloadReport(row),
    };
  };
  const editIcon = (row: any) => {
    return {
      tooltip: formatMessage({ id: 'law-portal.legal.plan.edit' }),
      icon: 'edit',
      onClick: (e) => {
        e.stopPropagation(); handleEditClick(row);
      },
    };
  };

  return (
    <>
      <div style={{ paddingTop: "10px", paddingLeft: "30px", paddingRight: "30px" }}>
      <MTable
        key={tableKey}
        tableRef={tableRef}
        columns={[
          {
            title: formatMessage({ id: 'law-portal.our-laws.table.number' }),
            field: 'number',
            hidden: false,
            sorting: false,

          },
          {
            title: formatMessage({ id: 'law-portal.our-laws.table.law' }),
            field: 'legislationName',
            width: 300, // fixed width in pixels
            cellStyle: { minWidth: 300, maxWidth: 300, whiteSpace: 'normal', flex: '0 0 300px' },
            headerStyle: { minWidth: 300, maxWidth: 300, whiteSpace: 'normal', flex: '0 0 300px' },
            render: (row: any) => <RepealableName name={row.legislationName} effectiveTo={null} />,
            hidden: false,
            sorting: false
          },
          {
            title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.comformities.description' }),
            field: 'answer',
            hidden: false,
            sorting: false,
            width: 350, // fixed width in pixels
            cellStyle: { minWidth: 500, maxWidth: 500, whiteSpace: 'normal', flex: '0 0 500px' },
            headerStyle: { minWidth: 500, maxWidth: 500, whiteSpace: 'normal', flex: '0 0 500px' },
          },
          {
            title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
            field: 'responsibleUser',
            render: (row: any) => {
              if (row.responsibleUser) {
                const responsiblearr = row?.responsibleUser?.split(',')
                return (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {responsiblearr?.map((val: any) => {
                        return <span>{val}</span>
                      })}
                    </div>
                  </>
                )
              } else {
                return "-"
              }
            },
            sorting: false,
            hidden: false
          },
          {
            title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.observations' }),
            field: 'compliance',
            // width: '35%',
            align: 'center',
            render: (row: any) => {
              return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 48 }}>
                  <RadioGroup row>
                    {row?.compliance === "deficiency" && (
                      <ComplianceReportCheckbox type="deficiency" value={row?.compliance} hidden={false} />
                    )}
                    {row?.compliance === "no" && (
                      <ComplianceReportCheckbox type="no" value={row?.compliance} hidden={false} />
                    )}
                    {row?.compliance === "yes" && (
                      <ComplianceReportCheckbox type="yes" value={row?.compliance} hidden={false} />
                    )}
                  </RadioGroup>
                </div>
              )
            },
            sorting: false,
            // hidden:true
          }
        ]}
        actions={[
          renderDownloadAction,
          //...(hasAccessToModule(Module.Administration|| hasAccessToModule(Module.Organization)) ? [editIcon] : [])
        ]}
        data={handleDataFetch}

      />
      {selectedRowData && modalOpen && <EditObservationModal open={modalOpen} setOpen={setModalOpen} rowData={selectedRowData} onSaveSuccess={handleSaveSuccess} />}
   </div>
    </>

  );
};
